import axios from "axios";
import { history } from "helpers";
import { request } from "service";
import { getbaseUrl } from "service/baserul";
import endpoints from "service/endpoints";
import {
  encryptField,
  loadKey,
  loadPublicKey,
  resetEncryptionKey,
} from "service/fieldEncryption";
import { Toast } from "service/toast";

export { loadKey, loadPublicKey, encryptField, resetEncryptionKey };

/**
 * ============================================================================
 * Authentication API Endpoints
 * ============================================================================
 */

/**
 * POST /admin/login
 * Submits email and encrypted password.
 * Returns either { token, roleId, ... } (direct login)
 * or { otpRequired: true, challengeId, maskedEmail, expiresIn, resendAfter }
 */
export const loginApi = async ({ userName, password }, isRetry = false) => {
  try {
    const encryptedPassword = await encryptField(password);
    const response = await request({
      url: endpoints.Endpoints.login,
      method: endpoints.APIMethods.POST,
      data: {
        userName: userName.trim(),
        password: encryptedPassword,
      },
    });
    return response?.data;
  } catch (err) {
    const reason = err?.data?.data?.reason || err?.data?.reason;
    if (reason === "ENCRYPTION_INVALID" && !isRetry) {
      resetEncryptionKey();
      return loginApi({ userName, password }, true);
    }
    throw err;
  }
};

/**
 * POST /admin/login/verify-otp
 * Verifies 6-digit OTP code for the given challengeId.
 */
export const verifyOtpApi = async ({ challengeId, otp }) => {
  const response = await request({
    url: endpoints.Endpoints.verifyOtp,
    method: endpoints.APIMethods.POST,
    data: {
      challengeId,
      otp: String(otp).trim(),
    },
  });
  return response?.data;
};

/**
 * POST /admin/login/resend-otp
 * Requests a new OTP challenge code.
 */
export const resendOtpApi = async ({ challengeId }) => {
  const response = await request({
    url: endpoints.Endpoints.resendOtp,
    method: endpoints.APIMethods.POST,
    data: {
      challengeId,
    },
  });
  return response?.data;
};

/**
 * POST /admin/user/forgotPassword
 * Dispatches password reset link to user's registered email.
 */
export const forgotPasswordApi = async ({ email }) => {
  const response = await request({
    url: endpoints.Endpoints.forgotPassword,
    method: endpoints.APIMethods.POST,
    data: {
      email: email.trim(),
    },
  });
  return response?.data;
};

/**
 * POST /admin/user/resetpassword (from email link)
 * Sets new password using reset token from URL.
 */
export const resetPasswordFromEmailApi = async (
  { token, newPassword, confirmPassword },
  isRetry = false
) => {
  try {
    const encNew = await encryptField(newPassword);
    const encConfirm = await encryptField(confirmPassword);

    const response = await request({
      url: endpoints.Endpoints.resetPassword,
      method: endpoints.APIMethods.POST,
      token: token,
      data: {
        newPassword: encNew,
        confirmPassword: encConfirm,
      },
    });
    return response?.data;
  } catch (err) {
    const reason = err?.data?.data?.reason || err?.data?.reason;
    if (reason === "ENCRYPTION_INVALID" && !isRetry) {
      resetEncryptionKey();
      return resetPasswordFromEmailApi(
        { token, newPassword, confirmPassword },
        true
      );
    }
    throw err;
  }
};

/**
 * POST /admin/user/resetpassword (from User Management)
 * Super admin resetting password for staff or self.
 */
export const resetUserPasswordApi = async (
  { userId, newPassword, confirmPassword },
  isRetry = false
) => {
  try {
    const encNew = await encryptField(newPassword);
    const encConfirm = await encryptField(confirmPassword);

    const response = await request({
      url: endpoints.userModule.userresetPassword,
      method: endpoints.APIMethods.POST,
      data: {
        userId,
        newPassword: encNew,
        confirmPassword: encConfirm,
      },
    });
    return response?.data;
  } catch (err) {
    const reason = err?.data?.data?.reason || err?.data?.reason;
    if (reason === "ENCRYPTION_INVALID" && !isRetry) {
      resetEncryptionKey();
      return resetUserPasswordApi(
        { userId, newPassword, confirmPassword },
        true
      );
    }
    throw err;
  }
};

/**
 * POST /admin/change-admin-password
 * Change password from Settings.
 */
export const changeAdminPasswordApi = async (
  { currentPassword, password, confirmPassword },
  isRetry = false
) => {
  try {
    const encCurrent = await encryptField(currentPassword);
    const encNew = await encryptField(password);
    const encConfirm = await encryptField(confirmPassword);

    const response = await request({
      url: endpoints.settings.changePassword,
      method: endpoints.APIMethods.POST,
      data: {
        currentPassword: encCurrent,
        password: encNew,
        confirmPassword: encConfirm,
      },
    });
    return response?.data;
  } catch (err) {
    const reason = err?.data?.data?.reason || err?.data?.reason;
    if (reason === "ENCRYPTION_INVALID" && !isRetry) {
      resetEncryptionKey();
      return changeAdminPasswordApi(
        { currentPassword, password, confirmPassword },
        true
      );
    }
    throw err;
  }
};

/**
 * POST /auth/logout
 * Invalidate server session, clear refresh cookie and client storage.
 */
export const logoutApi = async () => {
  try {
    const token = localStorage.getItem("token");
    await axios.post(
      `${getbaseUrl()}/auth/logout`,
      {},
      {
        withCredentials: true,
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
      }
    );
  } catch (err) {
    console.error("Logout API error:", err);
  } finally {
    localStorage.clear();
    history.push("/login");
  }
};

/**
 * Helper to persist login session in Redux and localStorage
 */
export const applyLoginSession = (data, dispatch, actions) => {
  if (!data?.token) return;

  localStorage.setItem("token", data.token);
  localStorage.setItem("firstPath", "/gold-maintenance");

  if (actions?.setRole && data?.roleId?.name) {
    dispatch(actions.setRole(data.roleId.name));
  }
  if (actions?.setPermittedSections && data?.roleId?.permissions) {
    dispatch(actions.setPermittedSections(data.roleId.permissions));
  }
  if (actions?.getName) {
    dispatch(
      actions.getName({
        _id: data?._id,
        name: data?.name,
        roleId: data?.roleId?._id,
      })
    );
  }

  history.push("/gold-maintenance");
};

export default {
  loadKey,
  loadPublicKey,
  encryptField,
  resetEncryptionKey,
  loginApi,
  verifyOtpApi,
  resendOtpApi,
  forgotPasswordApi,
  resetPasswordFromEmailApi,
  resetUserPasswordApi,
  changeAdminPasswordApi,
  logoutApi,
  applyLoginSession,
};
